import { useEffect, useSyncExternalStore } from "react";

/**
 * EIP-6963: Multi Injected Provider Discovery
 * Discovers all installed browser wallets (Rabby, MetaMask, Trust, OKX, Phantom, etc.)
 */

export interface EIP6963ProviderInfo {
  uuid: string;
  name: string;
  icon: string; // data URI
  rdns: string; // reverse DNS identifier
}

export interface EIP6963ProviderDetail {
  info: EIP6963ProviderInfo;
  provider: any; // EIP-1193 provider
}

interface EIP6963AnnounceProviderEvent extends Event {
  detail: EIP6963ProviderDetail;
}

// Singleton store for discovered providers
let providers: EIP6963ProviderDetail[] = [];
let listeners: Set<() => void> = new Set();

function notifyListeners() {
  listeners.forEach((l) => l());
}

function handleAnnounce(event: Event) {
  const e = event as EIP6963AnnounceProviderEvent;
  const { info } = e.detail;

  // Deduplicate by uuid
  if (providers.some((p) => p.info.uuid === info.uuid)) return;

  providers = [...providers, e.detail];
  notifyListeners();
}

// Start listening immediately on module load
if (typeof window !== "undefined") {
  window.addEventListener("eip6963:announceProvider", handleAnnounce);
  // Request providers to announce themselves
  window.dispatchEvent(new Event("eip6963:requestProvider"));
}

function subscribe(callback: () => void) {
  listeners.add(callback);
  return () => {
    listeners.delete(callback);
  };
}

function getSnapshot() {
  return providers;
}

/** Get discovered providers outside of React (for eager connect) */
export function getEIP6963Providers(): EIP6963ProviderDetail[] {
  return providers;
}

/**
 * Hook to get all EIP-6963 discovered wallet providers.
 * Returns an array of { info, provider } objects.
 */
export function useEIP6963Providers(): EIP6963ProviderDetail[] {
  const discovered = useSyncExternalStore(subscribe, getSnapshot);

  // Re-request on mount in case providers were registered late
  useEffect(() => {
    window.dispatchEvent(new Event("eip6963:requestProvider"));
  }, []);

  return discovered;
}
